export interface RandomProblemsSessionRegistry {
  markFetched: (storagePath: string) => void;
  hasFetched: (storagePath: string) => boolean;
  commitPathname: (pathname: string) => void;
  getRouteSnapshot: (pathname: string) => string;
  subscribe: (listener: () => void) => () => void;
  registerRouteExit: (pathname: string, handler: () => void) => () => void;
}

export interface RandomProblemsRequestGate {
  start: () => AbortController;
  isActive: (controller: AbortController) => boolean;
  finish: (controller: AbortController) => void;
  abort: () => void;
}

/**
 * 같은 JS 세션에서 RandomProblems가 출제한 경로와 라우트 visit을 추적한다.
 *
 * LayoutSwitch가 확정한 pathname 변경을 commit하면 직전 경로의 요청을 즉시 취소하고,
 * pathname과 모든 회차 네임스페이스의 표시를 지운 뒤 visit revision을 올린다.
 * 이렇게 하면 Next가 전환 중 이전 페이지 트리를 보존해 RandomProblems cleanup이 늦거나
 * 실행되지 않아도 실제 SPA 이탈과 같은 pathname 내부 리마운트를 구분할 수 있다.
 */
export function createRandomProblemsSessionRegistry(): RandomProblemsSessionRegistry {
  const fetchedPaths = new Set<string>();
  const routeRevisions = new Map<string, number>();
  const listeners = new Set<() => void>();
  const routeExitHandlers = new Map<string, Set<() => void>>();
  let activePathname: string | null = null;

  const clearFetchedPathname = (pathname: string) => {
    for (const storagePath of fetchedPaths) {
      if (
        storagePath === pathname ||
        storagePath.startsWith(`${pathname}#`)
      ) {
        fetchedPaths.delete(storagePath);
      }
    }
  };

  return {
    markFetched(storagePath) {
      fetchedPaths.add(storagePath);
    },
    hasFetched(storagePath) {
      return fetchedPaths.has(storagePath);
    },
    commitPathname(pathname) {
      if (activePathname === pathname) return;

      const previousPathname = activePathname;
      activePathname = pathname;

      if (previousPathname !== null) {
        const previousRouteExitHandlers = routeExitHandlers.get(previousPathname);
        for (const handler of previousRouteExitHandlers ?? []) {
          handler();
        }
        clearFetchedPathname(previousPathname);
        if (previousRouteExitHandlers) {
          routeRevisions.set(
            previousPathname,
            (routeRevisions.get(previousPathname) ?? 0) + 1,
          );
        }
      }

      for (const listener of listeners) listener();
    },
    getRouteSnapshot(pathname) {
      const revision = routeRevisions.get(pathname) ?? 0;
      return `${revision}:${activePathname === pathname ? "active" : "inactive"}`;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    registerRouteExit(pathname, handler) {
      const handlers = routeExitHandlers.get(pathname) ?? new Set<() => void>();
      handlers.add(handler);
      routeExitHandlers.set(pathname, handlers);

      return () => {
        handlers.delete(handler);
        if (handlers.size === 0) routeExitHandlers.delete(pathname);
      };
    },
  };
}

export const randomProblemsSessionRegistry = createRandomProblemsSessionRegistry();

/**
 * 한 RandomProblems 인스턴스에서 가장 최근 요청만 유효하게 만든다.
 * 페이지 이탈이나 새 요청 시작 시 이전 요청을 abort하고, 늦게 끝난 응답이
 * localStorage와 세션 레지스트리를 다시 덮어쓰지 못하도록 활성 여부를 제공한다.
 */
export function createRandomProblemsRequestGate(): RandomProblemsRequestGate {
  let activeController: AbortController | null = null;

  return {
    start() {
      activeController?.abort();
      activeController = new AbortController();
      return activeController;
    },
    isActive(controller) {
      return activeController === controller && !controller.signal.aborted;
    },
    finish(controller) {
      if (activeController === controller) {
        activeController = null;
      }
    },
    abort() {
      activeController?.abort();
      activeController = null;
    },
  };
}
